import React, { useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import RequestSenderDropdown from './RequestSenderDropdown';
import RequestBroadChannelDropdown from './RequestBroadChannelDropdown';
import RequestTypeDropdown from './RequestTypeDropdown';
import VorVehicleDropdown from './VorVehicleDropdown';
import { useGetRequestTypeDropdownQuery } from '../../../../api/endpoints/teamProductivityMeasurement';
import OldRequestNumberMultiSelect from './OldRequestNumberMultiSelect';
import { useSelector } from 'react-redux';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import DealerDropdown from '../../../../components/common/DealerDropdown';
import PersonByGroupDropdown from './PersonByGroupDropdown';

const STATUS_DRAFT = 5;
const STATUS_COMPLETED = 15;
const SENDER_SERVICE_DEALER = 1947; // Servis & Bayi
const SENDER_CUSTOMER = 1948; // Müşteri
const SENDER_OWN_PLAN = 1950; // Kendi İş Planı
const SENDER_EXPORT_OP = 1951; // İhracat Operasyon

const FormTeamProductivityMeasurement = ({ id, data, editMode, submitted, formData, handleChange, saveRecord, completeRecord, resetForm, setSubmitted, isCreating, isUpdating, isCompleting, onClose }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const userParams = useSelector(selectUserParams);

    const isCompleted = editMode && data?.status === STATUS_COMPLETED;
    const isDisabled = isCompleted;

    useEffect(() => {
        if (formData.requestSender === SENDER_OWN_PLAN) {
            const fullName = `${userParams?.firstName ?? ''} ${userParams?.lastName ?? ''}`.trim();
            if (formData.requestSenderName !== fullName) {
                handleChange('requestSenderName', fullName);
            }
        }
    }, [formData.requestSender, userParams, formData.requestSenderName, handleChange]);

    // VOR kontrolü için requestType listesini çek
    const { data: requestTypeOptions = [] } = useGetRequestTypeDropdownQuery({ languageId });

    const isVorSelected = useMemo(() => {
        if (!formData.requestType || !requestTypeOptions.length) return false;
        const selected = requestTypeOptions.find((item) => (item.iD || item.id || item.ID) === formData.requestType);
        return selected?.text?.toLowerCase().includes('vor') || false;
    }, [formData.requestType, requestTypeOptions]);

    const handleSave = async () => {
    setSubmitted(true);
    const payload = {
        ...(editMode && { id: parseInt(id) }),
        oldRequestNumbers: formData.oldRequestNumbers || [],
        requestSender: formData.requestSender,
        requestSenderName: formData.requestSenderName || '',
        requestSenderDealerId: formData.requestSenderDealerId || null,
        requestBroadChannel: formData.requestBroadChannel,
        requestType: formData.requestType,
        requestDetail: formData.requestDetail || '',
        howItConcluded: formData.howItConcluded || '',
        lessonLearned: formData.lessonLearned || '',
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        vorVehicle: formData.vorVehicle || null,
        stakeholderUnit: formData.stakeholderUnit || null
    };
    const success = await saveRecord(payload, editMode);
    if (success) onClose();
};

   const handleComplete = () => {
    confirmDialog({
        message: th('teamProductivity.messages.confirmComplete'),
        header: th('common.confirm'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: th('common.yes'),
        rejectLabel: th('common.no'),
        accept: async () => {
            const payload = {
                id: parseInt(id),
                oldRequestNumbers: formData.oldRequestNumbers || [],
                requestSender: formData.requestSender,
                requestSenderName: formData.requestSenderName || '',
                requestSenderDealerId: formData.requestSenderDealerId || null,
                requestBroadChannel: formData.requestBroadChannel,
                requestType: formData.requestType,
                requestDetail: formData.requestDetail || '',
                howItConcluded: formData.howItConcluded || '',
                lessonLearned: formData.lessonLearned || '',
                startDate: formData.startDate,
                endDate: formData.endDate || null,
                vorVehicle: formData.vorVehicle || null,
                stakeholderUnit: formData.stakeholderUnit || null
            };

            const saved = await saveRecord(payload, true);
            if (!saved) return;

            const success = await completeRecord(parseInt(id), formData.endDate);
            if (success) onClose();
        }
    });
};
    const fieldIcon = {
        oldRequestNumbers: 'pi pi-history',
        requestSender: 'pi pi-user',
        requestSenderName: 'pi pi-id-card',
        requestBroadChannel: 'pi pi-send',
        requestType: 'pi pi-tag',
        vorVehicle: 'pi pi-car',
        requestDetail: 'pi pi-file-edit',
        howItConcluded: 'pi pi-check-square',
        lessonLearned: 'pi pi-check-square',
        startDate: 'pi pi-calendar-plus',
        endDate: 'pi pi-calendar-minus',
        requestNumber: 'pi pi-hashtag'
    };

    const renderLabel = (icon, label) => (
        <label className="font-bold block mb-2">
            <i className={`${icon} mr-2`} style={{ color: '#0B3187' }}></i>
            {label}
        </label>
    );
    const renderRequestSenderName = () => {
        const senderId = formData.requestSender;

        // 1) Servis & Bayi / Müşteri → bayi dropdown'ı, seçilen bayi ADI + ID kaydedilir
        if (senderId === SENDER_SERVICE_DEALER || senderId === SENDER_CUSTOMER) {
            return (
                <DealerDropdown
                    value={formData.requestSenderDealer || null}
                    onChange={(val) => {
                        handleChange('requestSenderDealer', val);
                        handleChange('requestSenderName', '');
                        handleChange('requestSenderDealerId', val?.id || null);
                    }}
                    disabled={isDisabled}
                    rowCount={100}
                    className="w-full"
                />
            );
        }

        // 2) Kendi İş Planı → userParams'tan otomatik, readonly
        if (senderId === SENDER_OWN_PLAN) {
            return <InputText value={formData.requestSenderName} className="w-full" />;
        }

        // 3) İhracat Operasyon → serbest input
        if (senderId === SENDER_EXPORT_OP) {
            return <InputText value={formData.requestSenderName} onChange={(e) => handleChange('requestSenderName', e.target.value)} disabled={isDisabled} className="w-full" />;
        }

        // 4) Diğer gruplar → persons dropdown (group_lookup_id'ye göre)
        if (senderId) {
            return <PersonByGroupDropdown senderLookupId={senderId} value={formData.requestSenderName} onChange={(val) => handleChange('requestSenderName', val)} disabled={isDisabled} className="w-full" />;
        }

        // Gönderen seçilmemiş → boş, disabled
        return <InputText value={formData.requestSenderName} disabled className="w-full" />;
    };

    return (
        <>
            <ConfirmDialog />
            <div className="grid p-fluid">
                {/* Talep Numarası */}
                {editMode && (
                    <div className="col-12 mb-2">
                        {renderLabel(fieldIcon.requestNumber, th('teamProductivity.form.requestNumber'))}
                        <span className="ml-2 font-bold text-xl" style={{ color: '#0B3187' }}>
                            {data?.requestNumber}
                        </span>
                    </div>
                )}

                {/* Eski Talep No - full width */}
                <div className="col-12 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.oldRequestNumbers, th('teamProductivity.form.oldRequestNumbers'))}
                        <OldRequestNumberMultiSelect
                            value={formData.oldRequestNumbers}
                            onChange={(val) => handleChange('oldRequestNumbers', val)}
                            disabled={isDisabled}
                            className="w-full"
                            excludeRequestNumber={editMode ? data?.requestNumber : null}
                        />
                    </div>
                </div>

                {/* Satır 1: Talep Gönderen + İsim Soyisim */}
                <div className="col-12 md:col-6 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.requestSender, th('teamProductivity.form.requestSender'))}
                        <RequestSenderDropdown
                            value={formData.requestSender}
                            onChange={(val) => {
                                handleChange('requestSender', val);
                                handleChange('requestSenderName', '');
                                handleChange('requestSenderDealer', null);
                                handleChange('requestSenderDealerId', null);
                            }}
                            disabled={isDisabled}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="col-12 md:col-6 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.requestSenderName, th('teamProductivity.form.requestSenderName'))}
                        {renderRequestSenderName()}
                    </div>
                </div>

                {/* Satır 2: Talebin Geliş Kanalı + Talep Tipi */}
                <div className="col-12 md:col-6 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.requestBroadChannel, th('teamProductivity.form.requestBroadChannel'))}
                        <RequestBroadChannelDropdown value={formData.requestBroadChannel} onChange={(val) => handleChange('requestBroadChannel', val)} disabled={isDisabled} className="w-full" />
                    </div>
                </div>

                <div className="col-12 md:col-6 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.requestType, th('teamProductivity.form.requestType'))}
                        <RequestTypeDropdown
                            value={formData.requestType}
                            onChange={(val) => {
                                handleChange('requestType', val);
                                handleChange('vorVehicle', null);
                            }}
                            disabled={isDisabled}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Vor Araç - sadece VOR seçilince */}
                {isVorSelected && (
                    <div className="col-12 md:col-6 mb-2">
                        <div className="field">
                            {renderLabel(fieldIcon.vorVehicle, th('teamProductivity.form.vorVehicle'))}
                            <VorVehicleDropdown value={formData.vorVehicle} onChange={(val) => handleChange('vorVehicle', val)} disabled={isDisabled} className="w-full" />
                        </div>
                    </div>
                )}

                {/* Talep Detay - full */}
                <div className="col-12 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.requestDetail, th('teamProductivity.form.requestDetail'))}
                        <InputTextarea value={formData.requestDetail} onChange={(e) => handleChange('requestDetail', e.target.value)} disabled={isDisabled} rows={3} autoResize className="w-full" />
                    </div>
                </div>

                {/* Nasıl Sonuçlandı - full */}
                <div className="col-12 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.howItConcluded, th('teamProductivity.form.howItConcluded'))}
                        <InputTextarea value={formData.howItConcluded} onChange={(e) => handleChange('howItConcluded', e.target.value)} disabled={isDisabled} rows={3} autoResize className="w-full" />
                    </div>
                </div>

                {/* Lesson Learned - full */}
                <div className="col-12 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.lessonLearned, th('teamProductivity.form.lessonLearned'))}
                        <InputTextarea value={formData.lessonLearned} onChange={(e) => handleChange('lessonLearned', e.target.value)} disabled={isDisabled} rows={3} autoResize className="w-full" />
                    </div>
                </div>

                {/* Başlangıç + Bitiş Tarihi */}
                <div className="col-12 md:col-6 mb-2">
                    <div className="field">
                        {renderLabel(fieldIcon.startDate, th('teamProductivity.form.startDate'))}
                        <Calendar value={formData.startDate} onChange={(e) => handleChange('startDate', e.value)} disabled={isDisabled} showTime showSeconds showIcon dateFormat="dd/mm/yy" className="w-full" />
                    </div>
                </div>

                {editMode && (
                    <div className="col-12 md:col-6 mb-2">
                        <div className="field">
                            <label className="font-bold block mb-2">{th('teamProductivity.form.endDate')}</label>
                            <Calendar value={formData.endDate} onChange={(e) => handleChange('endDate', e.value)} disabled={isDisabled} showTime showSeconds showIcon dateFormat="dd/mm/yy" className="w-full" />
                        </div>
                    </div>
                )}
            </div>

            {/* Buttons */}
            <div className="flex justify-content-end gap-2 mt-3">
                {editMode && data?.status === STATUS_DRAFT && <Button label={th('teamProductivity.buttons.complete')} icon="pi pi-check-circle" className="p-button-success" onClick={handleComplete} loading={isCompleting} />}
                {!isCompleted && <Button label={editMode ? th('teamProductivity.buttons.update') : th('teamProductivity.buttons.create')} icon="pi pi-save" onClick={handleSave} loading={isCreating || isUpdating} />}
            </div>
        </>
    );
};

export default FormTeamProductivityMeasurement;
